import type { PropsWithChildren } from 'react';

import { isString } from 'es-toolkit';

import { LoadingOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import type { ButtonProps, TooltipProps } from 'antd';

import type { SkyLabelProps } from '@skynet/sky-label';
import { SkyLabel } from '@skynet/sky-label';

const disabledButton =
  'transition-opacity duration-150 ease-in-out half-transparent cursor-not-allowed';

const typeClsMap = {
  primary:
    'bg-slate-100 text-slate-900 hover:bg-sky-700 hover:text-white active:bg-sky-900 active:text-white',
  primaryDisabled: `bg-slate-100 text-slate-900 ${disabledButton}`,
  dark: 'bg-stone-900/80 text-white hover:bg-sky-700 hover:text-white active:bg-sky-900',
  darkDisabled: `bg-stone-900/80 text-white ${disabledButton}`,
  default: 'shadow-none hover:bg-sky-800 hover:text-white active:bg-sky-950 active:text-white',
  defaultDisabled: `shadow-none ${disabledButton}`,
  linkDisabled: `text-blue-500 ${disabledButton}`,
};

const dangerClsMap = {
  primary: 'bg-red-500 text-white hover:bg-red-800 hover:text-white active:bg-red-950',
  primaryDisabled: `bg-red-500 text-white ${disabledButton}`,
  dark: 'bg-red-500 text-white hover:bg-red-800 hover:text-white active:bg-red-950',
  darkDisabled: `bg-red-500 text-white ${disabledButton}`,
  default:
    'shadow-none border-red-500 hover:bg-red-800 hover:text-white text-red-300 active:bg-red-950',
  defaultDisabled: `shadow-none ${disabledButton}`,
  link: 'text-red-500 hover:text-red-700 active:text-red-900',
  linkDisabled: `text-red-500 ${disabledButton}`,
};

// if you change size of SkyButton it should be changed respectively in SkyNumber iconCls
const sizeClsMap = {
  xsmall: 'px-0 py-0 h-4 text-sm',
  small: 'text-sm',
  middle: 'h-9 px-4.25',
  large: 'h-14',
};

export interface SkyButtonProps extends PropsWithChildren<Omit<ButtonProps, 'type' | 'size'>> {
  type?: 'default' | 'primary' | 'link' | 'text' | 'dashed' | 'dark';
  size?: 'large' | 'middle' | 'small' | 'xsmall';
  truncate?: number;
  tooltip?: string | Omit<TooltipProps, 'children'>;
  labelOptions?: Omit<SkyLabelProps, 'children'>;
  compact?: boolean;
}

export const SkyButton = ({
  type = 'default',
  size = 'middle',
  disabled = false,
  className = '',
  children,
  htmlType = undefined,
  truncate = 0,
  tooltip = undefined,
  block = false,
  danger = false,
  labelOptions = undefined,
  onKeyDown = undefined,
  compact = false,
  loading,
  ...rest
}: SkyButtonProps) => {
  const typeKey = disabled ? `${type}Disabled` : type;
  const typeCls = danger
    ? (dangerClsMap as Record<string, string>)[typeKey] || ''
    : (typeClsMap as Record<string, string>)[typeKey] || '';
  const sizeCls = sizeClsMap[size];

  const compactCls = compact
    ? 'mr-0 rounded-none first:rounded-l-2xl last:rounded-r-2xl hover:relative hover:z-10 not-first:-ml-px'
    : '';

  const antdType: ButtonProps['type'] = type === 'dark' ? 'text' : type;

  const buttonOpts: ButtonProps = {
    type: antdType,
    size: size === 'xsmall' ? ('small' as const) : size,
    htmlType,
    className: `transition-colors duration-300 ease-linear ${typeCls} ${sizeCls} ${compactCls} ${className}`,
    disabled,
    block,
    onKeyDown,
    loading: loading === true ? { icon: <LoadingOutlined className="py-2" spin /> } : loading,
    ...rest,
  };

  const labelOpts = {
    ...(labelOptions ?? {}),
    className: labelOptions?.className ?? '',
  } as SkyLabelProps;

  if (truncate && isString(children) && children.length > truncate) {
    return (
      <Tooltip title={children}>
        <Button {...buttonOpts}>
          <SkyLabel {...labelOpts}>{children.slice(0, truncate)}...</SkyLabel>
        </Button>
      </Tooltip>
    );
  }

  if (tooltip) {
    const tooltipProps: Omit<TooltipProps, 'children'> = isString(tooltip)
      ? { title: tooltip, trigger: ['click', 'hover'] }
      : tooltip;
    return (
      <Tooltip {...tooltipProps}>
        <Button {...buttonOpts}>
          {children && <SkyLabel {...labelOpts}>{children}</SkyLabel>}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button {...buttonOpts}>{children && <SkyLabel {...labelOpts}>{children}</SkyLabel>}</Button>
  );
};
